import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TextField, TextAreaField, SelectField, ChipGroup, Field } from "@/components/Form";
import { OnboardingExit } from "@/components/OnboardingExit";
import { ReliabilityBadge } from "@/components/StatusPill";
import { Star } from "lucide-react";
import { WORKER_ROLES, SOFTWARE, LANGUAGES, EXPERIENCE, WORK_TYPES, RADIUS_OPTIONS } from "@/constants/options";
import { US_STATES } from "@/constants/states";

const empty = {
  worker_roles: [],
  zip_code: "",
  home_state: "",
  travel_radius: 15,
  preferred_zips: [],
  preferred_areas: [],
  minimum_rate: 0,
  years_experience: "",
  experience: [],
  languages: ["English"],
  software: [],
  work_types: [],
  credentials: {
    license_state: "NY",
    license_number: "",
    license_status: "Active",
    registration_state: "NY",
    certification: "",
    certification_number: "",
  },
};

export default function WorkerProfileForm({ onboarding = false }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const roleParam = params.get("role");
  const [form, setForm] = useState(empty);
  const [rel, setRel] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [zipsText, setZipsText] = useState("");
  const [areasText, setAreasText] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadProfile = useCallback(async () => {
    const { data } = await api.get("/worker/profile");
    setName(data.user?.name || "");
    setPhone(data.user?.phone || "");
    if (data.profile) {
      setForm({ ...empty, ...data.profile, credentials: { ...empty.credentials, ...(data.profile.credentials || {}) } });
      setZipsText((data.profile.preferred_zips || []).join(", "));
      setAreasText((data.profile.preferred_areas || []).join(", "));
    } else if (roleParam) {
      setForm((f) => ({ ...f, worker_roles: [roleParam] }));
    }
    if (!onboarding) {
      try {
        setRel((await api.get("/worker/reliability")).data);
      } catch {
        // reliability is optional on this page
      }
    }
  }, [onboarding, roleParam]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCred = (k, v) => setForm((f) => ({ ...f, credentials: { ...f.credentials, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api.put("/worker/profile", {
        ...form,
        name,
        phone,
        minimum_rate: Number(form.minimum_rate) || 0,
        travel_radius: Number(form.travel_radius),
        years_experience: form.years_experience === "" ? null : Number(form.years_experience),
        preferred_zips: zipsText.split(",").map((z) => z.trim()).filter(Boolean),
        preferred_areas: areasText.split(",").map((z) => z.trim()).filter(Boolean),
      });
      await refresh();
      setSaved(true);
      if (onboarding) navigate("/worker/availability");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const hasRole = (r) => form.worker_roles.includes(r);

  return (
    <div className="mx-auto max-w-3xl">
      {onboarding && <OnboardingExit backTo="/worker/npi" backLabel="Back to role & NPI step" />}
      <p className="overline">{onboarding ? "Step 1 of 2" : "Worker profile"}</p>      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
        {onboarding ? "Tell us where and how you work" : "Your profile & preferences"}
      </h1>
      <p className="mt-3 text-sm text-slate-600">
        Only your role, ZIP and travel radius are required. Everything else sharpens your matches.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6" data-testid="worker-profile-form">
        {!onboarding && rel && (
          <section className="card-surface space-y-4 p-6" data-testid="reliability-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-semibold">Your reliability</h2>
              <ReliabilityBadge reliability={rel} testId="my-reliability-badge" />
            </div>
            <div className="grid gap-4 sm:grid-cols-4">
              <div>
                <p className="overline">Completed</p>
                <p className="mt-1 font-display text-2xl font-semibold">{rel.completed_shifts}</p>
              </div>
              <div>
                <p className="overline">Avg rating</p>
                <p className="mt-1 font-display text-2xl font-semibold">{rel.avg_rating ?? "—"}</p>
              </div>
              <div>
                <p className="overline">Reviews</p>
                <p className="mt-1 font-display text-2xl font-semibold">{rel.rating_count}</p>
              </div>
              <div>
                <p className="overline">No-shows</p>
                <p className="mt-1 font-display text-2xl font-semibold">{rel.no_shows}</p>
              </div>
            </div>
            {rel.reviews?.length > 0 && (
              <div className="space-y-2 border-t border-slate-100 pt-4">
                {rel.reviews.map((r) => (
                  <div key={`${r.pharmacy_name}-${r.date}-${r.stars ?? "ns"}`} className="flex flex-wrap items-center gap-2 text-sm text-slate-600" data-testid={`review-${r.date}`}>
                    <span className="font-medium text-slate-800">{r.pharmacy_name}</span>
                    <span className="text-slate-400">{r.date}</span>
                    {r.no_show ? (
                      <span className="pill border-red-200 bg-red-50 text-red-700">No-show</span>
                    ) : (
                      <span className="flex items-center gap-1">
                        {r.stars} <Star className="h-3.5 w-3.5 fill-accentx text-accentx" />
                      </span>
                    )}
                    {r.comment && <span className="text-slate-500">"{r.comment}"</span>}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {form.npi_verified && (
          <section className="card-surface space-y-4 p-6" data-testid="worker-registry-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-semibold">From the NPI Registry</h2>
              <span className="pill border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="worker-npi-verified-badge">
                NPI verified
              </span>
            </div>
            <dl className="grid gap-4 sm:grid-cols-2">
              {[
                ["Registered name", form.npi_registry_name],
                ["NPI", form.npi],
                ["Home state", form.home_state],
                ["Home ZIP", form.zip_code],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="overline">{label}</dt>
                  <dd className="mt-1 text-sm font-medium text-slate-800">{value || "—"}</dd>
                </div>
              ))}
            </dl>
            {form.npi_taxonomies?.length > 0 && (
              <p className="text-xs text-slate-500">{form.npi_taxonomies.join(" · ")}</p>
            )}
            <p className="text-xs text-slate-500">
              Your pharmacist identity, home state and ZIP come from the official NPPES NPI Registry and
              cannot be edited here.
            </p>
          </section>
        )}

        <section className="card-surface space-y-5 p-6">
          <h2 className="font-display text-xl font-semibold">About you</h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Full name" value={name} onChange={(e) => setName(e.target.value)} testId="worker-name" />
            <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} testId="worker-phone" />
          </div>
          <SelectField
            label="Your role"
            required
            disabled={form.npi_verified}
            options={form.npi_verified
              ? [{ value: "Pharmacist", label: "Pharmacist" }]
              : [{ value: "", label: "Select your role" },
                 ...WORKER_ROLES.filter((r) => r !== "Pharmacist").map((r) => ({ value: r, label: r }))]}
            value={form.worker_roles[0] || ""}
            onChange={(e) => set("worker_roles", e.target.value ? [e.target.value] : [])}
            testId="worker-role-select"
            hint={form.npi_verified
              ? "Locked to Pharmacist by your verified NPI."
              : "Pharmacist requires NPI verification — start from the role & NPI step."}
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Years of experience"
              type="number"
              min="0"
              value={form.years_experience ?? ""}
              onChange={(e) => set("years_experience", e.target.value)}
              testId="worker-years"
            />
            <TextField
              label="Minimum pay ($/hour)"
              type="number"
              min="0"
              value={form.minimum_rate}
              onChange={(e) => set("minimum_rate", e.target.value)}
              testId="worker-min-rate"
            />
          </div>
        </section>

        <section className="card-surface space-y-5 p-6">
          <h2 className="font-display text-xl font-semibold">Where do you want to work?</h2>
          <div className="grid gap-5 sm:grid-cols-3">
            {form.npi_verified ? (
              <>
                <div>
                  <p className="field-label">Home ZIP</p>
                  <p className="mt-2 text-sm font-medium text-slate-800" data-testid="worker-zip-readonly">{form.zip_code || "—"}</p>
                  <p className="mt-1 text-xs text-slate-500">From your NPI record</p>
                </div>
                <div>
                  <p className="field-label">Home state</p>
                  <p className="mt-2 text-sm font-medium text-slate-800" data-testid="worker-home-state-readonly">{form.home_state || "—"}</p>
                  <p className="mt-1 text-xs text-slate-500">From your NPI record</p>
                </div>
              </>
            ) : (
              <>
                <TextField
                  label="Home ZIP"
                  required
                  value={form.zip_code}
                  onChange={(e) => set("zip_code", e.target.value)}
                  placeholder="11385"
                  testId="worker-zip"
                />
                <SelectField
                  label="Home state"
                  options={[{ value: "", label: "Select a state" }, ...US_STATES]}
                  value={form.home_state || ""}
                  onChange={(e) => set("home_state", e.target.value)}
                  testId="worker-home-state"
                />
              </>
            )}
            <SelectField
              label="Travel radius"
              options={RADIUS_OPTIONS.map((r) => ({ value: r, label: `${r} miles` }))}
              value={form.travel_radius}
              onChange={(e) => set("travel_radius", e.target.value)}
              testId="worker-radius"
            />
          </div>
          <TextField
            label="Preferred ZIP codes"
            value={zipsText}
            onChange={(e) => setZipsText(e.target.value)}
            placeholder="11373, 11374, 11385, 11386"
            hint="Comma separated and optional — if you leave this empty we match shifts within your travel radius of your home ZIP."
            testId="worker-preferred-zips"
          />
          <TextField
            label="Preferred neighborhoods (optional)"
            value={areasText}
            onChange={(e) => setAreasText(e.target.value)}
            placeholder="Ridgewood, Rego Park"
            testId="worker-preferred-areas"
          />
        </section>

        <section className="card-surface space-y-5 p-6">
          <h2 className="font-display text-xl font-semibold">Skills & preferences</h2>
          <ChipGroup label="Software experience" options={SOFTWARE} value={form.software} onChange={(v) => set("software", v)} testIdPrefix="worker-software" />
          <ChipGroup label="Languages" options={LANGUAGES} value={form.languages} onChange={(v) => set("languages", v)} testIdPrefix="worker-language" />
          <ChipGroup label="Experience" options={EXPERIENCE} value={form.experience} onChange={(v) => set("experience", v)} testIdPrefix="worker-experience" />
          <ChipGroup label="Work types" options={WORK_TYPES} value={form.work_types} onChange={(v) => set("work_types", v)} testIdPrefix="worker-worktype" />
        </section>

        {(hasRole("Pharmacist") || hasRole("Pharmacy Technician")) && (
          <section className="card-surface space-y-5 p-6">
            <h2 className="font-display text-xl font-semibold">Credentials</h2>
            {hasRole("Pharmacist") && (
              <div className="grid gap-5 sm:grid-cols-3">
                <SelectField label="License state" options={US_STATES} value={form.credentials.license_state || "NY"} onChange={(e) => setCred("license_state", e.target.value)} testId="cred-license-state" hint="Shifts are matched to the state you are licensed in." />
                <TextField label="License number" value={form.credentials.license_number || ""} onChange={(e) => setCred("license_number", e.target.value)} testId="cred-license-number" />
                <SelectField label="License status" options={["Active", "Inactive", "Pending"]} value={form.credentials.license_status || "Active"} onChange={(e) => setCred("license_status", e.target.value)} testId="cred-license-status" />
              </div>
            )}
            {hasRole("Pharmacy Technician") && (
              <div className="grid gap-5 sm:grid-cols-3">
                <SelectField label="State registration" options={US_STATES} value={form.credentials.registration_state || "NY"} onChange={(e) => setCred("registration_state", e.target.value)} testId="cred-registration-state" hint="State where you are registered to work as a technician." />
                <TextField label="Certification" value={form.credentials.certification || ""} onChange={(e) => setCred("certification", e.target.value)} placeholder="CPhT" testId="cred-certification" />
                <TextField label="Certification number" value={form.credentials.certification_number || ""} onChange={(e) => setCred("certification_number", e.target.value)} testId="cred-certification-number" />
              </div>
            )}
            <Field label="Verification status">
              <span className="pill border-slate-200 bg-slate-50 text-slate-600" data-testid="worker-verification-status">
                {user?.verification_status || "Unverified"}
              </span>
              <p className="mt-2 text-xs text-slate-500">
                Verification is automatic — you move to Pending verification when your first shift is
                confirmed, and to Verified once the pharmacy reviews that completed shift.
              </p>
            </Field>
          </section>
        )}

        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="worker-profile-error">{error}</p>}
        {saved && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="worker-profile-saved">Profile saved.</p>}

        <div className="flex gap-3">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="worker-profile-submit">
            {busy ? "Saving…" : onboarding ? "Continue to availability" : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}
